import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import RemoveIcon from "@mui/icons-material/Remove";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ChatCore from "../../containers/ChatUI/ChatCore";
import HeaderChatUI from "../../containers/ChatUI/HeaderChatUI";

const STORAGE_KEY = "ao_floating_chat_window";
const MIN_WIDTH = 340;
const MIN_HEIGHT = 260;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const getId = (value) => String(value?._id || value?.id || value || "").trim();

const getDefaultBox = () => ({
    x: Math.max(16, window.innerWidth - 470),
    y: Math.max(16, window.innerHeight - 720),
    width: 440,
    height: 680,
});

const readBox = () => {
    if (typeof window === "undefined") {
        return { x: 24, y: 24, width: 440, height: 680 };
    }

    try {
        const raw = localStorage.getItem(STORAGE_KEY);

        if (!raw) return getDefaultBox();

        const parsed = JSON.parse(raw);

        return {
            x: Number(parsed.x || getDefaultBox().x),
            y: Number(parsed.y || getDefaultBox().y),
            width: Number(parsed.width || 440),
            height: Number(parsed.height || 680),
        };
    } catch (_error) {
        return getDefaultBox();
    }
};

const saveBox = (box) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(box));
    } catch (_error) { }
};

const getChatTitle = (payload = {}) => {
    if (payload.title) return payload.title;

    const chat = payload.chat || {};

    if (chat.title) return chat.title;
    if (chat.entity?.type === "order") return chat.entity.title || "Чат заказа";
    if (chat.entity?.type === "product") return chat.entity.title || "Чат товара";
    if (chat.chatType === "saved") return "Сохранённое";

    return "Чат";
};

const getChatOfficeId = (payload = {}) => getId(
    payload.officeId ||
    payload.office?._id ||
    payload.office?.id ||
    payload.chat?.officeId
);

const FloatingChatHost = ({
    officeId: officeIdProp = null,
    office: officeProp = null,
    role: roleProp = null,
    onOpenFullChat,
}) => {
    const navigate = useNavigate();
    const location = useLocation();

    const authUser = useSelector((state) => state.auth?.user || {}, shallowEqual);
    const offices = useSelector((state) => state.office?.offices || [], shallowEqual);
    const activeOfficeId = useSelector((state) => state.office?.currentOfficeId || null);

    const [box, setBox] = useState(readBox);
    const [typingUsers, setTypingUsers] = useState([]);
    const [state, setState] = useState({
        open: false,
        minimized: false,
        expanded: false,
        chat: null,
        title: "Чат",
        source: null,
        officeId: null,
        office: null,
        role: null,
    });

    const dragRef = useRef(null);
    const resizeRef = useRef(null);

    useEffect(() => {
        const handleOpen = (event) => {
            const payload = event?.detail || {};
            const chat = payload.chat || null;

            if (!chat?._id) return;

            const nextOfficeId = getChatOfficeId(payload) || getId(officeIdProp) || null;
            const nextOffice = payload.office ||
                (nextOfficeId
                    ? (offices || []).find((item) => getId(item) === nextOfficeId) || null
                    : null) ||
                officeProp ||
                null;

            setTypingUsers([]);

            setState({
                open: true,
                minimized: false,
                expanded: false,
                chat,
                title: getChatTitle(payload),
                source: payload.source || null,
                officeId: nextOfficeId,
                office: nextOffice,
                role: payload.role || roleProp || authUser?.role || null,
            });
        };

        const handleClose = () => {
            setTypingUsers([]);
            setState((prev) => ({
                ...prev,
                open: false,
                minimized: false,
                expanded: false,
            }));
        };

        window.addEventListener("ao-floating-chat:open", handleOpen);
        window.addEventListener("ao-floating-chat:close", handleClose);

        return () => {
            window.removeEventListener("ao-floating-chat:open", handleOpen);
            window.removeEventListener("ao-floating-chat:close", handleClose);
        };
    }, [authUser?.role, officeIdProp, officeProp, offices, roleProp]);

    useEffect(() => {
        if (state.expanded || state.minimized) return;
        saveBox(box);
    }, [box, state.expanded, state.minimized]);

    useEffect(() => {
        if (!state.open || state.minimized) {
            setTypingUsers([]);
        }
    }, [state.open, state.minimized, state.chat?._id]);

    useEffect(() => {
        if (authUser?._id || authUser?.id) return;

        setState((prev) => prev.open ? {
            ...prev,
            open: false,
            minimized: false,
            expanded: false,
        } : prev);
    }, [authUser?._id, authUser?.id]);

    const bridgeManagesChat = useMemo(() => {
        if (!state.officeId) return false;
        if (location.pathname !== "/adminLayout") return false;

        return getId(activeOfficeId) === getId(state.officeId);
    }, [activeOfficeId, location.pathname, state.officeId]);

    const startDrag = useCallback((event) => {
        if (state.expanded) return;

        const point = event.touches?.[0] || event;

        dragRef.current = {
            startX: point.clientX,
            startY: point.clientY,
            box,
        };

        document.body.classList.add("floating-chat-dragging-AO");
    }, [box, state.expanded]);

    const startResize = useCallback((event, axis = "both") => {
        if (state.expanded || state.minimized) return;

        const point = event.touches?.[0] || event;

        resizeRef.current = {
            startX: point.clientX,
            startY: point.clientY,
            box,
            axis,
        };

        document.body.classList.add("floating-chat-dragging-AO");
        event.preventDefault();
        event.stopPropagation();
    }, [box, state.expanded, state.minimized]);

    useEffect(() => {
        const onMove = (event) => {
            const point = event.touches?.[0] || event;

            if (dragRef.current) {
                const dx = point.clientX - dragRef.current.startX;
                const dy = point.clientY - dragRef.current.startY;
                const startBox = dragRef.current.box;

                setBox((prev) => ({
                    ...prev,
                    x: clamp(startBox.x + dx, 8, window.innerWidth - Math.min(startBox.width, window.innerWidth) - 8),
                    y: clamp(startBox.y + dy, 8, window.innerHeight - 52),
                }));

                return;
            }

            if (resizeRef.current) {
                const dx = point.clientX - resizeRef.current.startX;
                const dy = point.clientY - resizeRef.current.startY;
                const startBox = resizeRef.current.box;
                const maxWidth = window.innerWidth - startBox.x - 8;
                const maxHeight = window.innerHeight - startBox.y - 8;

                const axis = resizeRef.current.axis || "both";

                setBox((prev) => ({
                    ...prev,
                    width: axis === "height"
                        ? prev.width
                        : clamp(startBox.width + dx, MIN_WIDTH, maxWidth),
                    height: clamp(startBox.height + dy, MIN_HEIGHT, maxHeight),
                }));
            }
        };

        const onEnd = () => {
            dragRef.current = null;
            resizeRef.current = null;
            document.body.classList.remove("floating-chat-dragging-AO");
        };

        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onEnd);
        window.addEventListener("touchmove", onMove, { passive: false });
        window.addEventListener("touchend", onEnd);

        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onEnd);
            window.removeEventListener("touchmove", onMove);
            window.removeEventListener("touchend", onEnd);
        };
    }, []);

    const handleOpenFull = () => {
        if (!state.chat?._id) return;

        if (onOpenFullChat) {
            onOpenFullChat(state.chat);
        } else if (state.officeId) {
            const params = new URLSearchParams();
            params.set("tab", "officeChat");
            params.set("office", state.officeId);
            params.set("chat", state.chat._id);

            navigate({
                pathname: "/adminLayout",
                search: `?${params.toString()}`,
            });
        } else {
            navigate(`/chat/${state.chat._id}`);
        }

        // Полный и плавающий экземпляры одного чата не должны одновременно
        // занимать экран. Оставляем быстрый свёрнутый ярлык для возврата.
        setState((prev) => ({
            ...prev,
            minimized: true,
            expanded: false,
        }));
    };

    if (!state.open || !state.chat?._id) return null;

    const style = state.expanded
        ? undefined
        : {
            left: box.x,
            top: state.minimized ? "auto" : box.y,
            bottom: state.minimized ? 16 : "auto",
            width: state.minimized ? 360 : box.width,
            height: state.minimized ? 52 : box.height,
        };

    return (
        <div
            className={[
                "floating-chat-AO",
                state.minimized ? "is-minimized" : "",
                state.expanded ? "is-expanded" : "",
            ].filter(Boolean).join(" ")}
            style={style}
        >
            <div
                className="floating-chat-AO__top"
                onMouseDown={startDrag}
                onTouchStart={startDrag}
            >
                <div className="floating-chat-AO__drag">
                    <DragIndicatorIcon sx={{ fontSize: 18 }} />
                </div>

                <HeaderChatUI
                    chat={state.chat}
                    userId={authUser?._id || authUser?.id || null}
                    officeId={state.officeId}
                    office={state.office}
                    variant="floating"
                    typingUsers={typingUsers}
                />

                <div className="floating-chat-AO__actions">
                    <button
                        type="button"
                        onMouseDown={(event) => event.stopPropagation()}
                        onClick={() => setState((prev) => ({ ...prev, minimized: !prev.minimized }))}
                        title={state.minimized ? "Развернуть" : "Свернуть"}
                    >
                        <RemoveIcon sx={{ fontSize: 17 }} />
                    </button>

                    <button
                        type="button"
                        onMouseDown={(event) => event.stopPropagation()}
                        onClick={handleOpenFull}
                        title="Открыть полный чат"
                    >
                        <OpenInFullIcon sx={{ fontSize: 17 }} />
                    </button>

                    <button
                        type="button"
                        onMouseDown={(event) => event.stopPropagation()}
                        onClick={() => setState((prev) => ({ ...prev, expanded: !prev.expanded, minimized: false }))}
                        title={state.expanded ? "Обычный размер" : "На весь экран"}
                    >
                        ⛶
                    </button>

                    <button
                        type="button"
                        onMouseDown={(event) => event.stopPropagation()}
                        onClick={() => setState((prev) => ({ ...prev, open: false }))}
                        title="Закрыть"
                    >
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </button>
                </div>
            </div>

            {!state.minimized && (
                <div className="floating-chat-AO__body">
                    <ChatCore
                        chat={state.chat}
                        officeId={state.officeId}
                        office={state.office}
                        role={state.role}
                        chats={[]}
                        realtimeManaged={bridgeManagesChat}
                        focusMessageId={null}
                        mode="floating"
                        onTypingUsersChange={setTypingUsers}
                    />
                </div>
            )}

            {!state.minimized && !state.expanded && (
                <>
                    <button
                        type="button"
                        className="floating-chat-AO__resize-y"
                        onMouseDown={(event) => startResize(event, "height")}
                        onTouchStart={(event) => startResize(event, "height")}
                        title="Изменить высоту"
                    />

                    <button
                        type="button"
                        className="floating-chat-AO__resize"
                        onMouseDown={(event) => startResize(event, "both")}
                        onTouchStart={(event) => startResize(event, "both")}
                        title="Изменить размер"
                    />
                </>
            )}
        </div>
    );
};

export default FloatingChatHost;
